import { useEffect, useState } from 'react';
import {
  inseminatorBoniterService,
  type InseminatorBoniterItem,
  type InseminatorBoniterFilters,
  type CreateInseminatorBoniterRequest,
} from '../../services/inseminator-boniter.service';
import { regionService, type RegionNodeDto } from '../../services/region.service';
import './AdminInseminators.css';

type TabKey = 'all' | 'inseminators' | 'boniters' | 'transplantologists';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'inseminators', label: 'Осеменаторы' },
  { key: 'boniters', label: 'Бонитеры' },
  { key: 'transplantologists', label: 'Трансплантологи' },
];

const SUBJECT_TYPES: { ru: string; kz: string }[] = [
  { ru: 'Техник-осеменатор', kz: 'Техник-ұрықтандырушы' },
  { ru: 'Бонитер-классификатор', kz: 'Бонитер-классификатор' },
  { ru: 'Трансплантолог', kz: 'Трансплантолог' },
];

const emptyForm: CreateInseminatorBoniterRequest = {
  regionId: 0,
  subjectTypeRu: SUBJECT_TYPES[0].ru,
  subjectTypeKz: SUBJECT_TYPES[0].kz,
  fullName: '',
  isCattle: false,
  startDate: new Date().toISOString().slice(0, 10),
  isActive: true,
};

const PAGE_SIZE = 20;

export default function AdminInseminators() {
  const [tab, setTab] = useState<TabKey>('all');
  const [items, setItems] = useState<InseminatorBoniterItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [regions, setRegions] = useState<RegionNodeDto[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateInseminatorBoniterRequest>({ ...emptyForm });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = (currentPage = page, currentSearch = search, currentTab = tab) => {
    setLoading(true);
    setError(null);
    const filters: InseminatorBoniterFilters = {
      page: currentPage,
      size: PAGE_SIZE,
      searchFullName: currentSearch || undefined,
    };
    const req =
      currentTab === 'inseminators'
        ? inseminatorBoniterService.getInseminators(filters)
        : currentTab === 'boniters'
        ? inseminatorBoniterService.getBoniters(filters)
        : currentTab === 'transplantologists'
        ? inseminatorBoniterService.getTransplantologists(filters)
        : inseminatorBoniterService.getAll(filters);

    req
      .then((res) => {
        if (res.success && res.data) {
          setItems(res.data.data);
          setTotalCount(res.data.totalCount);
        } else {
          setError(res.message || 'Ошибка загрузки');
        }
      })
      .catch(() => setError('Ошибка загрузки данных'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1, search, tab);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => {
    regionService
      .getChildren(null)
      .then((res) => {
        if (res.success && res.data) setRegions(res.data);
      })
      .catch(() => {});
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
    load(1, searchInput, tab);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const changePage = (next: number) => {
    setPage(next);
    load(next, search, tab);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setMessage(null);
    setShowForm(true);
  };

  const openEdit = (item: InseminatorBoniterItem) => {
    setEditingId(item.id);
    setForm({
      regionId: item.regionId,
      subjectTypeRu: item.subjectTypeRu,
      subjectTypeKz: item.subjectTypeKz,
      fullName: item.fullName,
      isCattle: item.isCattle,
      startDate: item.startDate.slice(0, 10),
      isActive: item.isActive,
    });
    setMessage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
    setMessage(null);
  };

  const handleSubjectTypeChange = (ru: string) => {
    const match = SUBJECT_TYPES.find((t) => t.ru === ru);
    setForm((f) => ({
      ...f,
      subjectTypeRu: ru,
      subjectTypeKz: match ? match.kz : f.subjectTypeKz,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fullName.trim()) {
      setMessage({ type: 'err', text: 'ФИО обязательно' });
      return;
    }
    if (!form.subjectTypeRu.trim()) {
      setMessage({ type: 'err', text: 'Тип специалиста обязателен' });
      return;
    }
    if (!form.regionId) {
      setMessage({ type: 'err', text: 'Регион обязателен' });
      return;
    }
    if (!form.startDate) {
      setMessage({ type: 'err', text: 'Дата начала обязательна' });
      return;
    }

    setSubmitLoading(true);
    setMessage(null);

    const request =
      editingId !== null
        ? inseminatorBoniterService.update(editingId, form)
        : inseminatorBoniterService.create(form);

    request
      .then((res) => {
        if (res.success) {
          setMessage({
            type: 'ok',
            text: editingId !== null ? 'Обновлено успешно' : 'Создано успешно',
          });
          load(page, search, tab);
          if (editingId === null) setForm({ ...emptyForm });
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка сохранения' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка сети' }))
      .finally(() => setSubmitLoading(false));
  };

  const handleDelete = (id: number) => {
    if (!confirm('Удалить специалиста?')) return;
    inseminatorBoniterService
      .delete(id)
      .then((res) => {
        if (res.success) load(page, search, tab);
      })
      .catch(() => {});
  };

  return (
    <div className="admin-insem">
      <div className="admin-insem-header">
        <div>
          <h1>Осеменаторы / Бонитеры</h1>
          <p className="admin-insem-desc">Список специалистов по типу</p>
        </div>
        <button className="admin-insem-add-btn" onClick={showForm ? closeForm : openCreate}>
          {showForm ? 'Отмена' : '+ Добавить'}
        </button>
      </div>

      {showForm && (
        <form className="admin-insem-form" onSubmit={handleSubmit}>
          <h2 className="admin-insem-form-title">
            {editingId !== null ? 'Редактировать специалиста' : 'Новый специалист'}
          </h2>
          <div className="admin-insem-form-grid">
            <div className="admin-insem-row admin-insem-row--full">
              <label>ФИО *</label>
              <input
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                placeholder="Фамилия Имя Отчество"
                maxLength={300}
              />
            </div>
            <div className="admin-insem-row">
              <label>Тип специалиста *</label>
              <select
                value={form.subjectTypeRu}
                onChange={(e) => handleSubjectTypeChange(e.target.value)}
              >
                {SUBJECT_TYPES.map((t) => (
                  <option key={t.ru} value={t.ru}>
                    {t.ru}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-insem-row">
              <label>Тип специалиста (каз)</label>
              <input
                value={form.subjectTypeKz}
                onChange={(e) => setForm((f) => ({ ...f, subjectTypeKz: e.target.value }))}
                placeholder="Казахское название"
                maxLength={200}
              />
            </div>
            <div className="admin-insem-row">
              <label>Регион *</label>
              <select
                value={form.regionId || ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, regionId: Number(e.target.value) }))
                }
              >
                <option value="">— не выбран —</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nameRu}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-insem-row">
              <label>Дата начала деятельности *</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              />
            </div>
            <div className="admin-insem-row admin-insem-row--checks">
              <label className="admin-insem-check">
                <input
                  type="checkbox"
                  checked={form.isCattle}
                  onChange={(e) => setForm((f) => ({ ...f, isCattle: e.target.checked }))}
                />
                Работает с КРС
              </label>
              <label className="admin-insem-check">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                Действующий
              </label>
            </div>
          </div>

          {message && (
            <p className={message.type === 'ok' ? 'admin-insem-msg-ok' : 'admin-insem-msg-err'}>
              {message.text}
            </p>
          )}

          <div className="admin-insem-actions">
            <button type="submit" disabled={submitLoading}>
              {submitLoading ? 'Сохранение...' : editingId !== null ? 'Сохранить' : 'Создать'}
            </button>
            <button type="button" className="admin-insem-cancel-btn" onClick={closeForm}>
              Отмена
            </button>
          </div>
        </form>
      )}

      <div className="admin-insem-tabs">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            className={`admin-insem-tab${tab === key ? ' admin-insem-tab--active' : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <form className="admin-insem-search" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Поиск по ФИО..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit">Найти</button>
      </form>

      {loading && <p className="admin-insem-loading">Загрузка...</p>}
      {!loading && error && <p className="admin-insem-error">{error}</p>}
      {!loading && !error && (
        <div className="admin-insem-table-wrap">
          <table className="admin-insem-table">
            <thead>
              <tr>
                <th>ФИО</th>
                <th>Тип</th>
                <th>Регион</th>
                <th>КРС</th>
                <th>Дата начала</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.fullName}</td>
                  <td>{item.subjectTypeRu}</td>
                  <td>{item.region?.nameRu ?? '—'}</td>
                  <td>
                    <span
                      className={`admin-insem-badge${item.isCattle ? ' admin-insem-badge--yes' : ' admin-insem-badge--no'}`}
                    >
                      {item.isCattle ? 'Да' : 'Нет'}
                    </span>
                  </td>
                  <td>{new Date(item.startDate).toLocaleDateString('ru-RU')}</td>
                  <td>
                    <span
                      className={`admin-insem-badge${item.isActive ? ' admin-insem-badge--yes' : ' admin-insem-badge--no'}`}
                    >
                      {item.isActive ? 'Действующий' : 'Недействующий'}
                    </span>
                  </td>
                  <td className="admin-insem-row-actions">
                    <button className="admin-insem-edit-btn" onClick={() => openEdit(item)}>
                      Изменить
                    </button>
                    <button className="admin-insem-del-btn" onClick={() => handleDelete(item.id)}>
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="admin-insem-empty">Нет специалистов</p>}
        </div>
      )}

      {totalPages > 1 && (
        <div className="admin-insem-pagination">
          <button onClick={() => changePage(page - 1)} disabled={page <= 1}>
            ← Назад
          </button>
          <span>
            Стр. {page} / {totalPages} (всего {totalCount})
          </span>
          <button onClick={() => changePage(page + 1)} disabled={page >= totalPages}>
            Вперёд →
          </button>
        </div>
      )}
    </div>
  );
}
